import { BehaviorSubject, createMutationObservable, filterDuplicates, isNonEmptyString } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectTargetingSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { CONFIG_RESOLVER } from '../../../loader/token.js';
import { GLOBAL_TARGETING } from '../../token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';

const log = logger.logGroup({ prefix: 'MetaKeywordsCollector' });

/**
 * Normalizes a unicode string and replace special characters to their ASCII equivalent.
 *
 * @param str the string to normalize
 * @returns the normalized string
 */
const normalize = (str: string): string => str
    // convert the string to lowercase
    .toLowerCase()
    // replace german umlaut characters with their non-umlaut equivalents using character encoding
    .replace(/\u00fc/g, 'ue').replace(/\u00e4/g, 'ae').replace(/\u00f6/g, 'oe').replace(/\u00df/g, 'ss')
    // remove diacritics (accents) using Unicode normalization (NFD) and regex
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * A TypeGuard to check if a given Node is a HTMLMetaElement.
 *
 * @param node the node to check
 * @returns true if the given node is a HTMLMetaElement
 */
const isMetaKeywordsElement = (node: Node): node is HTMLMetaElement => node.nodeName === 'META' && (node as HTMLMetaElement).name === 'keywords';

/**
 * A helper function to check if a given MutationRecord is a mutation of meta keywords.
 *
 * @param param0 a MutationRecord
 * @returns true if the given MutationRecord is a mutation of meta keywords
 */
const isMetaKeywordMutation = ({ type, addedNodes, removedNodes, target, attributeName }: MutationRecord): boolean => (
    // check if meta keywords node is added or removed
    (type === 'childList' && (Array.from(addedNodes).some(isMetaKeywordsElement) || Array.from(removedNodes).some(isMetaKeywordsElement))) ||
    // check if content attribute of meta keywords node is modified
    (type === 'attributes' && isMetaKeywordsElement(target) && attributeName === 'content')
);

@injectionTarget()
export class MetaKeywordsCollector {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;

    /** A observable that emits the current meta keywords. */
    #metaKeywords$ = new BehaviorSubject<string[]>([]);

    constructor() {
        this.#metaKeywords$
            // getting the previous and current keywords over time
            .scan<{ previous: string[], current: string[] }>(
                (acc, current) => ({ previous: acc.current, current }),
                { previous: [], current: [] },
            )
            .subscribe(({ previous, current }) => {
                if (previous.length) {
                    // remove keywords from globalTargeting
                    log.debug('remove keywords %o', [previous]);
                    this.globalTargeting.deleteValues('keywords', previous);
                }
                if (current.length) {
                    // adding keywords to globalTargeting
                    log.debug('add keywords %o', [current]);
                    this.globalTargeting.addValues('keywords', current);
                }
            });
    }

    @onPhase({ phase: Phase.TARGETING })
    public collectKeywords(): void {
        const { collectMetaKeys = 0 } = this.configResolver.get(selectTargetingSettings()) ?? {};
        // if its disabled in the config, we do not need to collect the meta keywords
        if (!collectMetaKeys) {
            log.debug('collectMetaKeys disabled in config');
            return;
        }

        // reading meta keywords from the DOM and write it to the signal
        const update = () => {
            const elements = Array.from(document.querySelectorAll('meta[name="keywords"]')).filter(isMetaKeywordsElement);
            const keywords = elements.map(element => element.content).filter(isNonEmptyString).join(',');
            const keywordArray = normalize(keywords).replace(/,\s?/g, '+').split('+').filter(isNonEmptyString).filter(filterDuplicates);
            this.#metaKeywords$.next(keywordArray.slice(0, collectMetaKeys));
        }

        // create a MutationObserver to monitor changes to meta keywords; whenever the meta keywords change, update the keywords
        createMutationObservable(document.head, { childList: true, subtree: true, attributes: true, attributeFilter: ['content'] })
            .flatMap(mutations => mutations)
            .filter(isMetaKeywordMutation)
            .subscribe(update);

        // update the keywords initially
        update();
    }
}
